import { useState } from "react";
import { loginToBubblaV, connectWixSite } from "../api";
import type { OAuthLoginProps } from "./component-types";

/**
 * Extract instanceId from Wix's signed `instance` URL parameter.
 * Wix passes this in the iframe URL for both Wix Editor and Business Manager.
 * Format: <signature>.<base64url-json>  — payload contains instanceId.
 */
function getInstanceIdFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const instanceParam = params.get("instance");
    if (!instanceParam) return null;
    const parts = instanceParam.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(payload));
    return json.instanceId ?? null;
  } catch {
    return null;
  }
}

export function OAuthLogin({ onComplete }: OAuthLoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Log into BubblaV to get an access token
      const loginResult = await loginToBubblaV(email, password);
      if (!loginResult) {
        setError("Invalid email or password.");
        return;
      }

      // Step 2: Get instanceId from Wix's signed `instance` URL parameter
      const instanceId = getInstanceIdFromUrl();
      if (!instanceId) {
        setError("Could not read Wix instance data. Make sure this panel is opened from Wix.");
        return;
      }
      const instanceData = { instanceId, siteId: instanceId, siteName: "", siteUrl: "" };

      // Step 3: Connect the Wix site to the BubblaV account.
      // The backend uses App ID + Secret + instanceId to authenticate with Wix
      // and sets the {{websiteId}} embed script parameter automatically.
      const connectResult = await connectWixSite(
        instanceData.instanceId,
        loginResult.accessToken,
        instanceData.siteName,
        instanceData.siteUrl,
      );

      if (!connectResult?.websiteId) {
        setError("Failed to connect your Wix site. Please try again.");
        return;
      }

      onComplete(connectResult.websiteId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connection failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="container">
      <div className="header">
        <h1>Connect BubblaV</h1>
        <p>Sign in to add an AI chatbot to your Wix site</p>
      </div>

      <form onSubmit={handleConnect}>
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: "12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className="button button-primary"
          disabled={isLoading}
          style={{ width: "100%" }}
        >
          {isLoading ? "Connecting..." : "Connect My Account"}
        </button>
      </form>
    </div>
  );
}
